import { Drawer as MuiDrawer, DrawerProps as MuiDrawerProps } from '@mui/material';
import { useMemo } from 'react';
import { useIsMobile } from '../../theme';
import { CommonDataProps } from '../../utils';
import { DrawerContent } from './DrawerContent';
import { DrawerContextProvider, DrawerContextProviderProps, DrawerContextValue } from './DrawerContextProvider';
import { DrawerFooter } from './DrawerFooter';
import { DrawerHeader } from './DrawerHeader';

export interface DrawerProps
  extends Pick<MuiDrawerProps, 'SlideProps' | 'aria-labelledby'>,
    Pick<Partial<DrawerContextValue>, 'variant' | 'size'>,
    CommonDataProps {
  /**
   * The contents of the drawer. Use the `Drawer.Header` and `Drawer.Content` subcomponents here.
   */
  children: React.ReactNode;
  /**
   * Handler for closing the drawer.
   */
  onClose: () => void;
  /**
   * Whether the drawer is open or closed.
   */
  open: boolean;

  /**
   * z-index applied to the drawer.
   */
  zIndex?: number;
}

export const Drawer = ({
  children,
  open,
  onClose,
  size = 'medium',
  SlideProps,
  variant = 'edit',
  zIndex,
  ...rest
}: DrawerProps) => {
  // Using viewport-based source for drawer layout because they are overlays that cover the page, so should
  // behave based on the viewport (not a specific element on the page).
  // This overrides the default breakpointSource (element).
  const isMobile = useIsMobile({ breakpointSource: 'viewport' });

  let widthStyles;
  const defaultStyle = { width: '85%', minWidth: 'min(900px, 100%)' };

  switch (size) {
    case 'small':
      widthStyles = { width: '500px' };
      break;
    case 'medium':
      widthStyles = { width: '900px' };
      break;
    case 'large-fluid':
      widthStyles = defaultStyle;
      break;
    default:
      widthStyles = { ...defaultStyle, maxWidth: '1200px' };
      break;
  }

  const drawerContextValue: DrawerContextProviderProps['value'] = useMemo(() => {
    return {
      variant,
      size,
    };
  }, [variant, size]);

  return (
    <DrawerContextProvider value={drawerContextValue}>
      <MuiDrawer
        aria-labelledby={rest['aria-labelledby']}
        data-testid={rest['data-testid']}
        data-trackid={rest['data-trackid']}
        open={open}
        onClose={onClose}
        anchor="right"
        variant="temporary"
        sx={{
          zIndex,
        }}
        PaperProps={{
          sx: {
            ...widthStyles,
            overflow: 'hidden',
            ...(isMobile && { width: '100%' }),
            maxWidth: '100%',
          },
        }}
        SlideProps={SlideProps}
      >
        {children}
      </MuiDrawer>
    </DrawerContextProvider>
  );
};

Drawer.Header = DrawerHeader;
Drawer.Content = DrawerContent;
Drawer.Footer = DrawerFooter;
